"use client";
import React, { FC, useCallback } from "react";
import Link from "next/link";
import { useFeatureFlags } from "@/components/shared/FeatureFlagsProvider";
import type { FeatureFlag } from "@/lib/feature-flags";
import { andrewIdentity } from "@/lib/seo/identity";

type FooterLink = { label: string; href: string; external?: boolean; flag?: FeatureFlag };

const consultingLinks: FooterLink[] = [
  { label: "Fractional leadership", href: "/consulting#fractional-leadership" },
  { label: "Websites with AI", href: "/consulting#website-builds" },
  { label: "Custom AI software", href: "/consulting#software-ai" },
];

const productLinks: FooterLink[] = [
  { label: "ServBuilder", href: "https://servbuilder.com", external: true },
];

const resourceLinks: FooterLink[] = [
  { label: "Home", href: "/" },
  { label: "Resume", href: "/resume" },
  { label: "Contact", href: "/contact" },
  { label: "DevOps contracts", href: "/hire" },
  { label: "Local websites", href: "/local" },
  { label: "Solutions", href: "/solutions" },
  { label: "About", href: "/about" },
  { label: "Video walkthroughs", href: "/video-walkthroughs", flag: "videoWalkthroughs" },
  { label: "Case studies", href: "/case-studies" },
  { label: "Free templates", href: "/free-templates", flag: "freeTemplates" },
];

const legalLinks: FooterLink[] = [
  { label: "Terms of Service", href: "/terms-of-service" },
  { label: "Refund Policy", href: "/refund-policy" },
  { label: "Privacy Statement", href: "/privacy-statement" },
  { label: "Cookie Policy", href: "/cookie-policy" },
];

function FooterLinkItem({ link }: { link: FooterLink }) {
  if (link.external) {
    return (
      <a className="flink" href={link.href} target="_blank" rel="noopener noreferrer">
        {link.label}
      </a>
    );
  }
  return (
    <Link className="flink" href={link.href}>
      {link.label}
    </Link>
  );
}

function ColumnHeading({ children, first = true }: { children: React.ReactNode; first?: boolean }) {
  return (
    <p className={`text-sm font-semibold text-slate-200 ${first ? "mb-2" : "mb-2 mt-4"}`}>{children}</p>
  );
}

const Footer: FC = () => {
  /**
   * Opens the Cookiebot consent dialog to allow users to update their cookie preferences.
   * @see https://www.cookiebot.com/en/developer/
   */
  const flags = useFeatureFlags();
  const handleCookieSettings = useCallback(() => {
    if (typeof window !== "undefined" && window.Cookiebot) {
      window.Cookiebot.renew();
    }
  }, []);

  return (
    <footer className="w-full border-t border-slate-800 bg-slate-950 pb-10 pt-24">
      <div className="wrap stack g48">
        <div className="grid gap-8 [grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr))]">
          <div className="stack g16">
            <Link href="/" aria-label="Andrew Leger, home" className="flex min-h-11 items-center gap-3 no-underline">
              <img src="/images/aleger-color-logo.svg" alt="" width={60} height={30} className="block h-[30px] w-[60px]" />
              <span className="text-xl font-bold uppercase leading-7 text-slate-200 [font-family:Arial,Helvetica,sans-serif]">
                Andrew Leger
              </span>
            </Link>
            <p className="small">AI-powered software and engineering leadership for small companies. Omaha, NE.</p>
            <div className="row g8">
              <a
                className="soc soc-li"
                href={andrewIdentity.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2zM4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4" />
                </svg>
              </a>
              <a
                className="soc soc-gh"
                href={andrewIdentity.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
              >
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
                </svg>
              </a>
              <a
                className="soc soc-x"
                href={andrewIdentity.twitter}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
              >
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4l16 16M20 4 4 20" />
                </svg>
              </a>
            </div>
          </div>

          <div className="stack gap-0.5">
            <ColumnHeading>Consulting Services</ColumnHeading>
            {consultingLinks.map((link) => (
              <FooterLinkItem key={link.href} link={link} />
            ))}
            <ColumnHeading first={false}>Products</ColumnHeading>
            {productLinks.map((link) => (
              <FooterLinkItem key={link.href} link={link} />
            ))}
          </div>

          <div className="stack gap-0.5">
            <ColumnHeading>Resources &amp; Support</ColumnHeading>
            {resourceLinks
              .filter((link) => !link.flag || flags[link.flag])
              .map((link) => (
                <FooterLinkItem key={link.href} link={link} />
              ))}
          </div>

          <div className="stack gap-0.5">
            <ColumnHeading>Legal &amp; Company</ColumnHeading>
            {legalLinks.map((link) => (
              <FooterLinkItem key={link.href} link={link} />
            ))}
            <button
              className="nav-btn min-h-11 justify-start py-3 pl-0 font-normal text-slate-400"
              type="button"
              onClick={handleCookieSettings}
            >
              Cookie settings
            </button>
          </div>
        </div>

        <div className="row justify-between gap-4 border-t border-slate-800 pt-6">
          <span className="caption">&copy; {new Date().getFullYear()} AJLDev, LLC. All Rights Reserved.</span>
          <a
            className="caption rounded border border-slate-700 px-2.5 py-1 no-underline"
            href="https://www.dmca.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            DMCA protected
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
